import { useState, useMemo } from "react";
import { RxType, RxFileFormat, Platform, ConnectionType } from "@/types/enums";
import { Rx } from "@/types/rx";
import { User } from "@/types/user";
import {
    UserConnection,
    FieldConnection,
    IrrigationSystemConnection,
} from "@/types/connection";
import {
    Button,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Autocomplete,
    AutocompleteItem,
    useDisclosure,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faFileExport,
    faFileZipper,
    faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import NewConnection from "../field/NewConnection";
import { useTranslation } from "react-i18next";

function RxExport({ rx, user }: { rx: Rx; user?: User }) {
    const { t } = useTranslation();
    const { userIsSentinel } = useAuth();
    const { downloadFile, useGetRequest, useMutateRequest } = useRequest();
    const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [formatModalOpen, setFormatModalOpen] = useState<boolean>(false);
    const [fileFormat, setFileFormat] = useState<RxFileFormat | null>(null);
    const fileFormatOptions = useMemo(() => {
        return rx.rx_type === RxType.ZONE
            ? [
                  {
                      key: RxFileFormat.JohnDeereMonitor,
                      label: "John Deere Monitor",
                  },
                  {
                      key: RxFileFormat.JohnDeereOpsCenter,
                      label: "John Deere Operations Center",
                  },
                  {
                      key: RxFileFormat.ValleyVRI,
                      label: "Valley VRI",
                  },
              ]
            : rx.rx_type === RxType.VRI
              ? [
                    {
                        key: RxFileFormat.FieldNET,
                        label: RxFileFormat.FieldNET,
                    },
                    {
                        key: RxFileFormat.FieldWISE,
                        label: RxFileFormat.FieldWISE,
                    },
                    {
                        key: RxFileFormat.AgSenseCommanderVP,
                        label: RxFileFormat.AgSenseCommanderVP,
                    },
                    {
                        key: RxFileFormat.AgSenseFieldCommander,
                        label: RxFileFormat.AgSenseFieldCommander,
                    },
                    {
                        key: RxFileFormat.AgSenseIconLink,
                        label: RxFileFormat.AgSenseIconLink,
                    },
                ]
              : [];
    }, [rx.rx_type]);
    const handleExportError = (error: unknown) => {
        if (error instanceof Error) {
            const resp = JSON.parse(error.message);
            setWarnings(resp?.warnings);
            setErrors(resp?.errors);
        }
        setWarningModalOpen(true);
    };
    const tryDownload = async (url: string) => {
        try {
            await downloadFile(url, rx.name);
        } catch (error) {
            handleExportError(error);
        } finally {
            setFormatModalOpen(false);
        }
    };
    const userConnections = useGetRequest<UserConnection[]>({
        url: `/user_connections/`,
        params: {
            user: user?.id,
        },
        enabled: !!user,
    });
    const fieldConnections = useGetRequest<FieldConnection[]>({
        url: `/field_connections/`,
        params: {
            field: rx.field,
        },
        enabled: !!rx.field,
    });
    const irrigationSystemConnections = useGetRequest<
        IrrigationSystemConnection[]
    >({
        url: `/irrigation_system_connections/`,
        params: {
            irrigation_system: rx.irrigation_system,
        },
        enabled: !!rx.irrigation_system,
    });
    const platformFilter: Platform[] = useMemo(() => {
        return rx.rx_type === RxType.ZONE
            ? [Platform.JohnDeere]
            : [Platform.FieldNET];
    }, [rx.rx_type]);
    const exportOptions = useMemo(() => {
        let options: {
            key: number;
            label: string;
            type: ConnectionType | "more";
        }[] = [];
        if (rx.rx_type === RxType.ZONE) {
            if (userConnections.data) {
                options = options.concat(
                    userConnections.data
                        .filter((connection) => {
                            return platformFilter.includes(connection.platform);
                        })
                        .map((connection) => {
                            return {
                                key: connection.id,
                                label: `My ${connection.platform}`,
                                type: ConnectionType.User,
                            };
                        }),
                );
            }
            if (fieldConnections.data) {
                options = options.concat(
                    fieldConnections.data
                        .filter((connection) => {
                            return platformFilter.includes(
                                connection.user_connection.platform,
                            );
                        })
                        .map((connection) => {
                            return {
                                key: connection.user_connection.id,
                                label: `${connection.user_connection.username} - ${connection.user_connection.platform} (Connected)`,
                                type: ConnectionType.User,
                            };
                        }),
                );
            }
        } else if (irrigationSystemConnections.data) {
            options = options.concat(
                irrigationSystemConnections.data
                    .filter((connection) => {
                        return platformFilter.includes(
                            connection.user_connection.platform,
                        );
                    })
                    .map((connection) => {
                        return {
                            key: connection.id,
                            label: `${connection.user_connection.user === user?.id ? "My" : connection.user_connection.username} ${connection.user_connection.platform} (Connected)`,
                            type: ConnectionType.IrrigationSystem,
                        };
                    }),
            );
        }
        if (userIsSentinel) {
            options = options.concat([
                {
                    key: 0,
                    label: "More Connections...",
                    type: "more",
                },
            ]);
        }
        return options;
    }, [
        userConnections.data,
        fieldConnections.data,
        irrigationSystemConnections.data,
        rx.rx_type,
        user?.id,
        userIsSentinel,
        platformFilter,
    ]);
    const [action, setAction] = useState<"download" | "export">("download");
    const [connectionId, setConnectionId] = useState<number>(0);
    const [connectionType, setConnectionType] = useState<
        | "user_connections"
        | "field_connections"
        | "irrigation_system_connections"
    >("user_connections");

    const exportRequest = useMutateRequest({
        method: "put",
        successMessage: t("success.exported_rx", { ns: "messages" }),
    });
    const exportRx = async (
        connectionType:
            | "user_connections"
            | "field_connections"
            | "irrigation_system_connections",
        connectionId: number,
        acceptWarnings?: boolean,
        organizationId?: string,
    ) => {
        try {
            await exportRequest.mutateAsync({
                url: `/${connectionType}/${connectionId}/prescriptions/`,
                data: organizationId
                    ? {
                          organization: organizationId,
                          prescription: rx.id,
                      }
                    : {
                          prescription: rx.id,
                      },
                params: {
                    accept_warnings: acceptWarnings,
                },
            });
        } catch (error) {
            handleExportError(error);
        }
    };
    const { isOpen, onOpenChange } = useDisclosure();
    return (
        <>
            <div className="flex flex-row w-full gap-2 items-center px-4 pb-1">
                <Dropdown>
                    <DropdownTrigger>
                        <Button color="primary" className="w-1/2">
                            <FontAwesomeIcon icon={faDownload} />
                            {t("download", { ns: "rx" })}
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                        {rx.rx_type === RxType.VRF ? (
                            <DropdownItem
                                key="file-download"
                                onPress={async () => {
                                    setAction("download");
                                    await tryDownload(
                                        `/prescriptions/${rx.id}/download/`,
                                    );
                                }}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faFileZipper}
                                        className="text-primary"
                                    />
                                }
                            >
                                File
                            </DropdownItem>
                        ) : (
                            <DropdownItem
                                key="file-download"
                                onPress={() => {
                                    setFormatModalOpen(true);
                                    setAction("download");
                                }}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faFileZipper}
                                        className="text-primary"
                                    />
                                }
                            >
                                {t("file", { ns: "rx" })}
                            </DropdownItem>
                        )}
                        <DropdownItem
                            key="report-download"
                            startContent={
                                <FontAwesomeIcon
                                    icon={faFilePdf}
                                    className="text-primary"
                                />
                            }
                            onPress={async () => {
                                await downloadFile(
                                    `/prescriptions/${rx.id}/report/`,
                                    `${rx.name} Report`,
                                );
                            }}
                        >
                            {t("report", { ns: "rx" })}
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
                <Dropdown>
                    <DropdownTrigger>
                        <Button color="primary" className="w-1/2">
                            <FontAwesomeIcon icon={faFileExport} />
                            {t("export_rx", { ns: "rx" })}
                        </Button>
                    </DropdownTrigger>
                    <DropdownMenu>
                        {exportOptions.map((option) => (
                            <DropdownItem
                                key={option.key}
                                onPress={() => {
                                    setAction("export");
                                    setConnectionId(option.key);
                                    if (option.type === "more") {
                                        onOpenChange();
                                    } else {
                                        setConnectionType(option.type);
                                        exportRx(option.type, option.key);
                                    }
                                }}
                            >
                                {option.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            </div>

            <Modal
                isOpen={formatModalOpen}
                onClose={() => setFormatModalOpen(false)}
            >
                <ModalContent>
                    <ModalHeader>
                        <h2 className="text-lg font-bold">
                            {t("file_format", { ns: "rx" })}
                        </h2>
                    </ModalHeader>
                    <ModalBody>
                        <div className="flex flex-col gap-2">
                            <Autocomplete
                                label={t("file_format", { ns: "rx" })}
                                placeholder={t("optional", { ns: "rx" })}
                                defaultItems={fileFormatOptions}
                                onSelectionChange={(item) => {
                                    setFileFormat(item as RxFileFormat);
                                }}
                            >
                                {(item) => (
                                    <AutocompleteItem key={item.key}>
                                        {item.label}
                                    </AutocompleteItem>
                                )}
                            </Autocomplete>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="primary"
                            onPress={async () => {
                                await tryDownload(
                                    fileFormat
                                        ? `/prescriptions/${rx.id}/download/?file_format=${fileFormat}`
                                        : `/prescriptions/${rx.id}/download/`,
                                );
                            }}
                        >
                            {t("download", { ns: "rx" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={warningModalOpen}
                onClose={() => setWarningModalOpen(false)}
            >
                <ModalContent>
                    <ModalBody>
                        {errors?.length > 0 && (
                            <div className="text-danger">
                                <h3 className="font-bold">
                                    {t("errors", { ns: "rx" })}:
                                </h3>
                                <ul className="list-disc pl-5">
                                    {errors.map((error, index) => (
                                        <li key={index}>{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {warnings?.length > 0 && (
                            <div className="text-warning">
                                <h3 className="font-bold">
                                    {t("warnings", { ns: "rx" })}:
                                </h3>
                                <ul className="list-disc pl-5">
                                    {warnings.map((warning, index) => (
                                        <li key={index}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {errors.length === 0 && action === "download" && (
                            <Button
                                color="warning"
                                onPress={async () => {
                                    await downloadFile(
                                        fileFormat
                                            ? `/prescriptions/${rx.id}/download/?accept_warnings=true&file_format=${fileFormat}`
                                            : `/prescriptions/${rx.id}/download/?accept_warnings=true`,
                                        `${rx.name}`,
                                    );
                                    setWarningModalOpen(false);
                                    setWarnings([]);
                                }}
                            >
                                {t("download_with_warnings", { ns: "rx" })}
                            </Button>
                        )}
                        {errors.length === 0 && action === "export" && (
                            <Button
                                color="warning"
                                onPress={() => {
                                    exportRx(
                                        connectionType,
                                        connectionId,
                                        true,
                                    );
                                    setWarningModalOpen(false);
                                    setWarnings([]);
                                }}
                            >
                                {t("export_with_warnings", { ns: "rx" })}
                            </Button>
                        )}
                        <Button onPress={() => setWarningModalOpen(false)}>
                            {t("close", { ns: "rx" })}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>
                        {t("export_to_a_connected_platform", { ns: "rx" })}
                    </ModalHeader>
                    <ModalBody>
                        <NewConnection
                            fieldId={rx.field}
                            rxId={rx.id}
                            platformFilter={platformFilter}
                            onFinish={(
                                connectionType: ConnectionType,
                                connectionId: number,
                                externalId?: string,
                            ) => {
                                exportRx(
                                    connectionType,
                                    connectionId,
                                    false,
                                    externalId,
                                );
                                onOpenChange();
                            }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default RxExport;
